import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { auctionService, reviewService } from '../services/api';

interface AuctionBid {
  id: number;
  amount: number;
  bidder?: {
    id: number;
    displayName: string;
    profilePicture?: string;
  };
  createdAt: string;
}

export interface AuctionDetail {
  id: number;
  title?: string;
  status: string;
  description?: string;
  facebookLink: string;
  publicToken: string;
  startPrice: number;
  currentPrice: number;
  minIncrement: number;
  startTime: string;
  endTime: string;
  imageUrls?: string;
  batchId?: string; // Added batchId
  // Legacy single card
  card?: {
    id: number;
    name: string;
    imageUrl?: string;
    rarity?: string;
    type?: string;
    cardNumber?: string;
    language?: string;
  };
  // New multi-item
  items?: {
    id: number;
    card: {
      id: number;
      name: string;
      imageUrl?: string;
      rarity?: string;
      type?: string;
      cardNumber?: string;
      language?: string;
    }
  }[];
  relatedAuctions?: AuctionDetail[];
  seller?: {
    id: number;
    displayName: string;
  };
  bids?: AuctionBid[];
}

const AuctionDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [auction, setAuction] = useState<AuctionDetail | null>(null);
  const [bids, setBids] = useState<AuctionBid[]>([]);
  const [bidAmount, setBidAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [placingBid, setPlacingBid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sellerRating, setSellerRating] = useState<{ averageRating: number; totalReviews: number } | null>(null);
  const isAuthenticated = Boolean(localStorage.getItem('token'));
  const userStr = localStorage.getItem('user');
  // @ts-ignore
  let currentUser: any = null;
  if (userStr) {
    try {
      currentUser = JSON.parse(userStr);
    } catch (e) {
      console.error("Error parsing user", e);
    }
  }

  useEffect(() => {
    if (id) {
      fetchAuction();
    }
  }, [id]);

  useEffect(() => {
    if (auction?.seller?.id) {
      fetchSellerRating(auction.seller.id);
    }
  }, [auction?.seller?.id]);

  const fetchAuction = async () => {
    try {
      setLoading(true);
      const response = await auctionService.getAuctionById(id!);
      const data = response.data.data as AuctionDetail;
      setAuction(data);
      setBids(data.bids || []);
      const minBid = calculateMinBid(data, data.bids || []);
      setBidAmount(minBid.toFixed(0));
    } catch (err) {
      console.error('Failed to fetch auction', err);
      // If 404/403, might be invalid token/ID
      setError(t('auctions.auctionDetail.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const fetchSellerRating = async (sellerId: number) => {
    try {
      const response = await reviewService.getUserReviews(String(sellerId), { page: 1, limit: 1 });
      setSellerRating({
        averageRating: response.data.data.summary?.averageRating || 0,
        totalReviews: response.data.data.summary?.totalReviews || 0
      });
    } catch (err) {
      console.error('Failed to fetch seller rating', err);
      setSellerRating(null);
    }
  };

  const calculateMinBid = (auctionData: AuctionDetail | null, bidList: AuctionBid[]) => {
    if (!auctionData) return 0;
    const highest = bidList.reduce((max, bid) => Math.max(max, Number(bid.amount)), 0);
    if (highest > 0) {
      return highest + Number(auctionData.minIncrement || 0);
    }
    return Number(auctionData.startPrice || 0);
  };

  const primaryImage = useMemo(() => {
    if (!auction) return null;
    if (auction.imageUrls) {
      try {
        const parsed = JSON.parse(auction.imageUrls);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed[0];
        }
      } catch (_e) {
        // ignore
      }
    }
    // Fallback if no specific img, use first card img
    if (auction.items && auction.items.length > 0) {
      return auction.items[0].card?.imageUrl;
    }
    return auction.card?.imageUrl;
  }, [auction]);

  const handlePlaceBid = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!auction || !id) return;

    if (!isAuthenticated) {
      // Redirect to login, preserving intent?
      // For now just redirect
      navigate('/login');
      return;
    }

    try {
      setPlacingBid(true);
      // Use auction.id in case we navigated here via Public Token (UUID) and Place Bid needs numeric ID
      // But typically API route /auctions/:id/bid works either way if controller handles it.
      // Controller uses findByPk(id) - UUID might fail if ID is int pk.
      // However getAuctionById handles UUID lookup. But placeBid assumes findByPk(id) which on generic Model uses PK (int).
      // So we should use auction.id (int) for the bid call if `id` param was UUID.
      const response = await auctionService.placeBid(String(auction.id), Number(bidAmount));
      const updatedAuction = response.data.data.auction as AuctionDetail;
      setAuction(updatedAuction);
      setBids(updatedAuction?.bids || []);
      const minBid = calculateMinBid(updatedAuction, updatedAuction?.bids || []);
      setBidAmount(minBid.toFixed(0));
      setError(null);
    } catch (err: any) {
      const message = err?.response?.data?.message || t('auctions.auctionDetail.failedToBid');
      setError(message);
    } finally {
      setPlacingBid(false);
    }
  };

  const handleCancelAuction = async () => {
    if (!auction?.id) return;
    if (!window.confirm(t('auctions.auctionDetail.cancelConfirm'))) {
      return;
    }

    try {
      await auctionService.cancelAuction(String(auction.id));
      alert(t('auctions.auctionDetail.cancelSuccess'));
      fetchAuction();
    } catch (err: any) {
      const message = err?.response?.data?.message || t('auctions.auctionDetail.failedCancel');
      alert(message);
    }
  };

  const countdown = auction
    ? formatDistanceToNow(new Date(auction.endTime), { addSuffix: true })
    : '';

  const renderStars = (rating: number) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className={star <= rating ? 'text-amber-500' : 'text-gray-300'}>
          ★
        </span>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('auctions.auctionDetail.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white p-6 rounded-lg shadow text-center">
          <p className="text-gray-600 mb-4">{t('auctions.auctionDetail.auctionNotFound')}</p>
          <Link to="/auctions" className="text-blue-600 hover:underline">
            {t('auctions.auctionDetail.backToAuctions')}
          </Link>
        </div>
      </div>
    );
  }

  const minBid = calculateMinBid(auction, bids);
  const biddingOpen = auction.status === 'LIVE';

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <div className="flex justify-between items-center mb-4">
        {auction.batchId ? (
          <Link to={`/auctions/batch/${auction.batchId}`} className="text-gray-500 hover:text-blue-600">
            ← {t('auctions.auctionDetail.backToBatch')}
          </Link>
        ) : (
          <Link to="/auctions" className="text-gray-500 hover:text-blue-600">
            ← {t('auctions.auctionDetail.backToAuctions')}
          </Link>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left Col: Images & Seller */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow overflow-hidden max-w-[400px] mx-auto lg:mx-0">
            <div className="aspect-[3/4] bg-gray-100 flex items-center justify-center relative">
              {primaryImage ? (
                <img src={primaryImage} alt={auction.title} className="w-full h-full object-contain p-2" />
              ) : (
                <div className="text-6xl text-gray-400">🎴</div>
              )}
              {/* Status Badge Overlay */}
              <span className={`absolute top-2 left-2 px-3 py-1 text-xs font-bold rounded text-white ${auction.status === 'LIVE' ? 'bg-green-600' : 'bg-gray-500'}`}>
                {auction.status}
              </span>
            </div>
            <div className="p-4 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">{t('auctions.auctionDetail.seller')}</p>
                  {auction.seller?.id ? (
                    <Link
                      to={`/users/${auction.seller.id}/reviews`}
                      className="font-semibold text-blue-700 hover:underline"
                    >
                      {auction.seller.displayName}
                    </Link>
                  ) : (
                    <p className="font-semibold text-gray-800">{auction.seller?.displayName || t('auctions.auctionDetail.unknown')}</p>
                  )}
                  {sellerRating && (
                    <div className="flex items-center gap-2 text-xs text-gray-600 mt-1">
                      {renderStars(Math.round(sellerRating.averageRating))}
                      <span className="font-semibold text-gray-800">
                        {sellerRating.averageRating.toFixed(1)}
                      </span>
                      <span className="text-gray-500">({sellerRating.totalReviews})</span>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">{t('auctions.auctionDetail.ends')}</p>
                  <p className="font-semibold text-gray-800">{countdown}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Col: Details & Bidding */}
        <div>
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-2xl font-bold">
                {auction.title || auction.card?.name || 'Auction'}
              </h1>
            </div>

            {auction.facebookLink && (
              <div className="mb-4">
                <a href={auction.facebookLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-blue-600 bg-blue-50 px-3 py-2 rounded hover:bg-blue-100 transition">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                  <span className="font-medium">{t('auctions.auctionDetail.verifyOnFacebook')}</span>
                </a>
                <p className="text-xs text-gray-500 mt-1">{t('auctions.auctionDetail.verifyReputation')}</p>
              </div>
            )}

            {auction.description && <p className="text-gray-700 mb-4 whitespace-pre-wrap">{auction.description}</p>}

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 bg-gray-50 rounded">
                <p className="text-xs text-gray-500">{t('auctions.auctionDetail.currentBid')}</p>
                <p className="text-3xl font-bold text-blue-700">
                  {Number(auction.currentPrice || auction.startPrice).toLocaleString('en-US', { maximumFractionDigits: 0 })} THB
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded">
                <p className="text-xs text-gray-500">{t('auctions.auctionDetail.minNextBid')}</p>
                <p className="text-2xl font-semibold text-gray-800">{minBid.toLocaleString('en-US', { maximumFractionDigits: 0 })} THB</p>
              </div>
            </div>

            <form onSubmit={handlePlaceBid} className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">{t('auctions.auctionDetail.yourBid')}</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min={minBid}
                  step={auction.minIncrement || 1}
                  value={bidAmount}
                  onChange={(e) => setBidAmount(e.target.value)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={!biddingOpen || placingBid || !isAuthenticated}
                />
                <button
                  type="submit"
                  disabled={!biddingOpen || placingBid || !isAuthenticated}
                  className={`px-6 py-3 rounded-lg font-semibold transition ${biddingOpen && isAuthenticated
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-600'
                    }`}
                >
                  {placingBid ? t('auctions.auctionDetail.placingBid') : t('auctions.auctionDetail.placeBid')}
                </button>
              </div>
              {error && <p className="text-red-600 text-sm">{error}</p>}
              {!isAuthenticated && (
                <p className="text-sm text-gray-500 text-left mt-2">
                  <Link to="/login" className="text-blue-600 underline">{t('auctions.auctionDetail.loginToBid')}</Link>
                </p>
              )}
            </form>
          </div>

          {/* Cancel Button (Owner or Admin Only) */}
          {(isAuthenticated && (currentUser?.id === auction.seller?.id || currentUser?.role === 'ADMIN') && auction.status === 'LIVE') && (
            <div className="container mx-auto px-4 mb-8">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <h3 className="text-red-800 font-semibold">{t('auctions.auctionDetail.manageAuction')}</h3>
                  <p className="text-red-600 text-sm">{t('auctions.auctionDetail.cancelNoBids')}</p>
                </div>
                <button
                  onClick={handleCancelAuction}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition"
                >
                  {t('auctions.auctionDetail.cancelAuction')}
                </button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">{t('auctions.auctionDetail.bidHistory')}</h2>
              <span className="text-sm text-gray-500">{t('auctions.auctionDetail.bidsCount', { count: bids.length })}</span>
            </div>
            {bids.length === 0 ? (
              <p className="text-gray-500 text-sm">{t('auctions.auctionDetail.noBids')}</p>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                {bids.map((bid) => (
                  <div key={bid.id} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                    <div>
                      <p className="font-semibold text-gray-800">{bid.bidder?.displayName || t('auctions.auctionDetail.bidder')}</p>
                      <p className="text-xs text-gray-500">{formatDistanceToNow(new Date(bid.createdAt), { addSuffix: true })}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-blue-700">{Number(bid.amount).toLocaleString('en-US', { maximumFractionDigits: 0 })} THB</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div >
  );
};

export default AuctionDetailPage;
